/**
 * Constructors for every node of a score.
 *
 * Nothing here decides anything musical; it exists so that a document is never assembled
 * by hand from object literals. Two things go wrong when it is. An id gets forgotten or
 * reused, and the document quietly loses the property the rest of the package depends on.
 * And a bar gets added to {@link Score.measures} without the matching entry in every
 * part's {@link Part.measures}, leaving parts that disagree about how long the piece is.
 * {@link addMeasure} and {@link addPart} keep both sides in step, and are the only
 * supported ways to change the shape of a score.
 */

import { createId } from "./ids";
import type { MeasureId, PartId } from "./ids";
import { plain } from "./duration";
import type { Duration } from "./duration";
import { AT_PITCH } from "./pitch";
import { DEFAULT_LAYOUT } from "./layout";
import type { Layout } from "./layout";
import type { NotePitch } from "./pitch";
import { C_MAJOR, COMMON_TIME, TREBLE } from "./types";
import type {
  Clef,
  GlobalMeasure,
  Instrument,
  KeySignature,
  MusicEvent,
  NoteEvent,
  Part,
  PartMeasure,
  RestEvent,
  Score,
  ScoreMeta,
  Staff,
  TimeSignature,
  Voice,
} from "./types";

/** Options for a new part. Anything left out takes a single-staff, non-transposing default. */
export interface PartOptions {
  readonly abbreviation?: string;
  readonly instrument?: Partial<Instrument>;
  readonly clefs?: readonly Clef[];
  readonly staffCount?: number;
  /** Lines in each staff: five unless the part is a drum written on one line. */
  readonly lines?: number;
}

/** A note, or a chord when given more than one pitch. */
export function note(pitches: readonly NotePitch[], duration: Duration): NoteEvent {
  return {
    id: createId("event"),
    kind: "note",
    duration,
    pitches,
    stem: "auto",
    beam: "auto",
    articulations: [],
    ornaments: [],
    arpeggio: null,
    tremolo: 0,
    fermata: null,
    cue: false,
    offset: null,
    staff: null,
    grace: null,
    lyrics: [],
  };
}

/**
 * A copy of an event with a new id, and without a tie reaching outside the run it is in.
 *
 * `first` and `last` say where the event stands in what is being copied: a tie is a link
 * between two notes, so one whose other end was left behind has to go, or the copy names a
 * note that is not there. A note drawn across onto another staff keeps that staff only
 * where there are `staves` to reach -- a passage copied out of a keyboard part and put
 * into a part written on one staff has nowhere to reach, and a staff that is not there
 * would be drawn nowhere.
 */
export function copyOfEvent(
  event: MusicEvent,
  first: boolean,
  last: boolean,
  staves: number,
): MusicEvent {
  const id = createId("event");
  const staff = event.staff !== null && event.staff >= staves ? null : event.staff;
  if (event.kind === "rest") return { ...event, id, staff };
  return {
    ...event,
    id,
    staff,
    pitches: event.pitches.map((head) => ({
      ...head,
      tieStop: head.tieStop && !first,
      tieStart: head.tieStart && !last,
    })),
  };
}

/** A rest. `wholeMeasure` draws the bar's single centred rest rather than a counted one. */
export function rest(duration: Duration, wholeMeasure = false): RestEvent {
  return {
    id: createId("event"),
    kind: "rest",
    duration,
    wholeMeasure,
    fermata: null,
    cue: false,
    offset: null,
    staff: null,
  };
}

/**
 * An empty line of music on the given staff.
 *
 * `ossia` makes it an alternative reading of the bar rather than a line played beside the
 * others: same staff, drawn small above it. See {@link Voice.ossia}.
 */
export function voice(staff: number, events: readonly MusicEvent[] = [], ossia = false): Voice {
  return { id: createId("voice"), staff, events, ossia };
}

/** A bar of silence for a part with `staffCount` staves. */
export function emptyPartMeasure(staffCount: number): PartMeasure {
  const voices: Voice[] = [];
  for (let staff = 0; staff < staffCount; staff += 1) {
    voices.push(voice(staff, [rest(plain("whole"), true)]));
  }
  return { voices, instrument: null, clefChanges: {}, directions: [], harmonies: [], figures: [] };
}

/** A bar of the piece that changes nothing about the metre or key. */
export function globalMeasure(overrides: Partial<Omit<GlobalMeasure, "id">> = {}): GlobalMeasure {
  return {
    id: createId("measure"),
    time: null,
    key: null,
    tempo: null,
    barline: "regular",
    repeat: null,
    jump: null,
    rehearsal: null,
    breakBefore: null,
    length: null,
    ...overrides,
  };
}

function staffAt(clefs: readonly Clef[], index: number, lines: number): Staff {
  return { id: createId("staff"), clef: clefs[index] ?? TREBLE, lines };
}

/**
 * A part with one silent bar for each bar the score already has.
 *
 * Taking the measure ids rather than a count is what keeps a part's bars addressable by
 * the global bar they fill; a part built from a count alone could not be joined to a score.
 */
export function createPart(
  name: string,
  measureIds: readonly MeasureId[],
  options: PartOptions = {},
): Part {
  const staffCount = options.staffCount ?? options.clefs?.length ?? 1;
  const clefs = options.clefs ?? [];
  const staves: Staff[] = [];
  const lines = options.lines ?? 5;
  for (let index = 0; index < staffCount; index += 1) staves.push(staffAt(clefs, index, lines));
  const measures: Record<MeasureId, PartMeasure> = {};
  for (const id of measureIds) measures[id] = emptyPartMeasure(staffCount);

  return {
    id: createId("part"),
    name,
    abbreviation: options.abbreviation ?? name.slice(0, 3),
    instrument: {
      name,
      sound: null,
      midiProgram: null,
      transposition: AT_PITCH,
      percussion: null,
      tuning: null,
      keyPrinting: "derived",
      ...options.instrument,
    },
    staves,
    measures,
    spanners: [],
  };
}

/** Default score header, with only the title filled in. */
export function scoreMeta(title: string, overrides: Partial<ScoreMeta> = {}): ScoreMeta {
  return {
    title,
    subtitle: null,
    composer: null,
    arranger: null,
    lyricist: null,
    copyright: null,
    movementNumber: null,
    movementTitle: null,
    ...overrides,
  };
}

/** Options for a new score. */
export interface ScoreOptions {
  readonly meta?: Partial<ScoreMeta>;
  readonly time?: TimeSignature;
  readonly key?: KeySignature;
  readonly measures?: number;
  /** The page it is set on; A4 with a 7mm staff when nothing is said. */
  readonly layout?: Layout;
}

/**
 * A new score: one bar, no parts, metre and key stated on the first bar.
 *
 * The first bar always carries a time signature and a key signature even when they are the
 * defaults, because {@link resolveAttributes} reads them by walking back from a bar until
 * it finds one, and a score whose first bar states neither has no answer to give.
 */
export function createScore(title: string, options: ScoreOptions = {}): Score {
  const count = Math.max(1, options.measures ?? 1);
  const measures: GlobalMeasure[] = [
    globalMeasure({ time: options.time ?? COMMON_TIME, key: options.key ?? C_MAJOR }),
  ];
  for (let index = 1; index < count; index += 1) measures.push(globalMeasure());

  return {
    id: createId("score"),
    meta: scoreMeta(title, options.meta),
    layout: options.layout ?? DEFAULT_LAYOUT,
    measures,
    parts: [],
    groups: [],
  };
}

/** Add a part, giving it a silent bar for every bar the score has. */
export function addPart(score: Score, name: string, options: PartOptions = {}): Score {
  const ids = score.measures.map((measure) => measure.id);
  return { ...score, parts: [...score.parts, createPart(name, ids, options)] };
}

/**
 * Insert a bar at `index`, or append it when `index` is omitted, filling it with silence
 * in every part.
 */
export function addMeasure(
  score: Score,
  overrides: Partial<Omit<GlobalMeasure, "id">> = {},
  index?: number,
): Score {
  const measure = globalMeasure(overrides);
  const at = index ?? score.measures.length;
  const measures = [...score.measures];
  measures.splice(Math.max(0, Math.min(at, measures.length)), 0, measure);

  const parts = score.parts.map((part) => ({
    ...part,
    measures: { ...part.measures, [measure.id]: emptyPartMeasure(part.staves.length) },
  }));

  return { ...score, measures, parts };
}

/** Remove a bar and every part's copy of it. A score never drops below one bar. */
export function removeMeasure(score: Score, id: MeasureId): Score {
  if (score.measures.length <= 1) return score;
  const parts = score.parts.map((part) => {
    const measures = { ...part.measures };
    delete measures[id];
    return { ...part, measures };
  });
  return { ...score, measures: score.measures.filter((m) => m.id !== id), parts };
}

/** Remove a part, and drop it from any group that listed it. */
export function removePart(score: Score, id: PartId): Score {
  return {
    ...score,
    parts: score.parts.filter((part) => part.id !== id),
    groups: score.groups
      .map((group) => ({ ...group, parts: group.parts.filter((part) => part !== id) }))
      .filter((group) => group.parts.length > 0),
  };
}
